/**
 * Orchard: blog "incompatible apps" widget (<incompatible-apps>).
 *
 * Lists the App Store apps whose current version requires a newer iOS than
 * the article's target version. Data comes live from the same database as
 * the app-compat checker (GET /v1/app-compat/incompat-list) and the verdict
 * uses the checker's own isOsVersionAtLeast, so the blog tables and the
 * checker always agree. Replaces the frozen static.getorchard.com snapshot
 * (2025-09-03) + unpkg compare-versions pair, which had every article
 * rendering "all apps are compatible".
 */
import { parseVersionParts } from '@orchard/app-compat-core';

const DATA_URL = 'https://api.getorchard.com/v1/app-compat/incompat-list';

const escapeHtml = (text) => {
  const div = document.createElement('div');
  div.textContent = text || '';
  return div.innerHTML;
};

/** Major component of a dotted version string, or null when unparseable. */
const majorOf = (version) => {
  const parts = parseVersionParts(version);
  return parts === null ? null : parts[0];
};

/**
 * Compatibility is judged on major versions only: the articles speak of
 * "iOS 18" meaning the whole 18.x line, since a device on iOS 18 takes
 * point updates within its major. An app requiring 18.6 is therefore
 * compatible with iOS 18; only apps demanding a newer major (26+) are
 * listed. Unparseable requirements stay listed — same conservative stance
 * as the checker's isOsVersionAtLeast.
 */
const incompatibleApps = (apps, iosVersion) => {
  const pageMajor = majorOf(iosVersion);
  if (pageMajor === null) return [];
  return apps
    .filter((app) => {
      const requiredMajor = majorOf(app.required_ios);
      return requiredMajor === null || requiredMajor > pageMajor;
    })
    .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { sensitivity: 'base' }));
};

const renderApp = (app, showUrls) => {
  const name = showUrls && app.url
    ? `<a href="${escapeHtml(app.url)}" target="_blank" rel="nofollow noopener noreferrer">${escapeHtml(app.name)}</a>`
    : escapeHtml(app.name);
  return `
    <div class="app-name">${name}</div>
    <div class="app-ios">${escapeHtml(app.required_ios)}</div>
  `;
};

const renderApps = (apps, showUrls, iosVersion) => {
  if (apps.length === 0) {
    const displayVersion = majorOf(iosVersion) ?? iosVersion;
    return `
      <div class="app-name">All apps are compatible with iOS ${escapeHtml(String(displayVersion))}</div>
      <div class="app-ios"></div>
    `;
  }
  const header = `
    <div class="grid-header app-name">App</div>
    <div class="grid-header app-ios">Required iOS</div>
  `;
  return header + apps.map((app) => renderApp(app, showUrls)).join('');
};

const renderError = (error) => `<div class="error">${escapeHtml(error)}</div>`;
const renderLoading = () => '<div class="loading">Loading app data...</div>';

class IncompatibleAppsWidget extends HTMLElement {
  static get observedAttributes() {
    return ['ios-version'];
  }

  connectedCallback() {
    this.innerHTML = '<div class="incompatible-apps-grid"></div>';
    this._load();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name === 'ios-version' && oldValue !== newValue && this._apps) {
      this._display();
    }
  }

  get iosVersion() { return this.getAttribute('ios-version') || ''; }
  get showUrls() { return this.getAttribute('show-urls') !== 'false'; }
  get dataUrl() { return this.getAttribute('data-url') || DATA_URL; }

  async _load() {
    if (this._apps || this._loading) return;
    this._loading = true;
    this._update(renderLoading());
    try {
      const response = await fetch(this.dataUrl);
      if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      const data = await response.json();
      this._apps = Array.isArray(data.apps) ? data.apps : [];
      this._display();
    } catch (error) {
      console.error('Error loading app incompatibility data:', error);
      this._update(renderError('Failed to load app data'));
    } finally {
      this._loading = false;
    }
  }

  _display() {
    if (!this._apps) return;
    const apps = incompatibleApps(this._apps, this.iosVersion);
    this._update(renderApps(apps, this.showUrls, this.iosVersion));
  }

  _update(html) {
    const container = this.querySelector('.incompatible-apps-grid');
    if (container) container.innerHTML = html;
  }
}

customElements.define('incompatible-apps', IncompatibleAppsWidget);
